import { forwardRef, type ReactNode } from 'react'
import { ComboboxComponents } from '@/components/ui/combobox'
import { ChevronDownIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/helpers/utils'

interface ComboboxTriggerProps {
	/** Label content shown on the left of the field (e.g. the selected value). */
	children: ReactNode
	id?: string
	hasError?: boolean
	disabled?: boolean
	/** Show a spinner in place of the chevron while options load. */
	loading?: boolean
}

/**
 * Shared styled combobox trigger: the bordered field with error/disabled states
 * and a chevron that flips open, swapped for a spinner while loading. Used by
 * both the single-select `ComboboxField` and the `ManagerMultiSelect`.
 */
export const ComboboxTrigger = forwardRef<HTMLDivElement, ComboboxTriggerProps>(
	function ComboboxTrigger(
		{ children, id, hasError, disabled, loading },
		ref
	) {
		return (
			<ComboboxComponents.Trigger
				id={id}
				nativeButton={false}
				render={
					<div
						className={cn(
							'flex h-11 w-full cursor-pointer items-center justify-between gap-2 rounded-lg border bg-neutral-white px-3.5 text-base transition-all duration-150',
							'data-[popup-open]:shadow-focus-primary-100-xxs data-[popup-open]:outline data-[popup-open]:outline-1',
							hasError
								? 'border-danger-300 data-[popup-open]:outline-danger-300'
								: 'border-neutral-300 data-[popup-open]:outline-neutral-300',
							disabled && 'cursor-not-allowed opacity-50'
						)}
						ref={ref}
					>
						{children}
						{loading ? (
							<Spinner
								className='ml-auto shrink-0 text-neutral-500'
								size={16}
							/>
						) : (
							<ComboboxComponents.Icon className='ml-auto shrink-0 text-neutral-500'>
								<ChevronDownIcon
									className='transition-transform duration-150 [[data-popup-open]_&]:rotate-180'
									size={16}
								/>
							</ComboboxComponents.Icon>
						)}
					</div>
				}
			/>
		)
	}
)

ComboboxTrigger.displayName = 'ComboboxTrigger'
